/**
 * Prefab - instantiation and prefab management for reusable actor templates.
 */
import { AssetPath } from '../../storage/storageProvider.js';

import { createLoader } from './factories.js';
import { MissingPrefabDataError, PrefabManager } from './prefabManager.js';
import { isLegacyData, LoaderFlags } from './types.js';

import type { LoaderOptions, PrefabInfo, SerializedData } from './types.js';
import type { Actor } from '../../actors/Actor.js';
import type * as THREE from 'three';

type WorldTransformInput = {
  position?: THREE.Vector3;
  rotation?: THREE.Euler;
  scale?: THREE.Vector3;
};

interface HasSetWorldTransform {
  setWorldTransform(transform: WorldTransformInput): void;
}

function hasSetWorldTransform(value: unknown): value is HasSetWorldTransform {
  return typeof (value as HasSetWorldTransform)?.setWorldTransform === 'function';
}

function applyInstantiationTransform(instance: unknown, options?: InstantiationOptions): void {
  if (!hasSetWorldTransform(instance)) {
    return;
  }
  instance.setWorldTransform({
    position: options?.position,
    rotation: options?.rotation,
    scale: options?.scale,
  });
}


export function setAsPrefabInstance(object: any, prefabPath: string | AssetPath | null): void {
  if (Array.isArray(object)) {
    throw new Error('Prefab root cannot be an array');
  }

  if (!prefabPath) {
    delete object.$prefab;
    return object;
  }

  const $prefab: PrefabInfo = {
    $path: prefabPath instanceof AssetPath ? prefabPath.initialPath : prefabPath,
  };
  object.$prefab = $prefab;
  return object;
}

export interface InstantiationOptions {
  withoutEditor?: boolean
  position?: THREE.Vector3;
  rotation?: THREE.Euler;
  scale?: THREE.Vector3;
}

export function instantiatePrefab<T = any>(prefabPath: string | AssetPath, prefabData?: Record<string, SerializedData>, options?: InstantiationOptions): T {
  // sync version, must make sure data is ready
  const instance = loadPrefabTemplateInstance(prefabPath, prefabData, options, true) as T;
  if (instance) {
    setAsPrefabInstance(instance, prefabPath);
    applyInstantiationTransform(instance, options);
  }
  return instance;
}

export function loadPrefabTemplateInstance(
  prefabPath: string | AssetPath,
  prefabData?: Record<string, SerializedData>,
  options?: InstantiationOptions,
  throwOnMissing: boolean = false,
): any {
  if (prefabData && Object.values(prefabData).some(data => data === null || typeof data !== 'object')) {
    throw new Error('Invalid prefab data');
  }

  for (const [path, data] of Object.entries(prefabData ?? {})) {
    PrefabManager.setPrefabData(path, data);
  }

  try {
    const data = PrefabManager.getPrefabDataChecked(prefabPath);
    const loaderOptions: LoaderOptions = {};
    if (options?.withoutEditor) {
      loaderOptions.flags = LoaderFlags.WithoutEditor;
    }
    const loader = createLoader(loaderOptions);
    const instance = loader.load(data);
    return instance;
  }
  catch (error) {
    if (!throwOnMissing && error instanceof MissingPrefabDataError) {
      return null;
    }
    throw error;
  }
}

export async function instantiatePrefabAsync<T = any>(prefabPath: string | AssetPath, options?: InstantiationOptions): Promise<T> {
  // async version, load data first
  const instance = await loadPrefabTemplateInstanceAsync(prefabPath, options) as T;
  setAsPrefabInstance(instance, prefabPath);
  applyInstantiationTransform(instance, options);
  return instance;
}

export async function loadPrefabTemplateInstanceAsync(prefabPath: string | AssetPath, options?: InstantiationOptions): Promise<any> {
  const data = await PrefabManager.getOrLoadPrefabData(prefabPath);
  const loaderOptions: LoaderOptions = {};
  if (options?.withoutEditor) {
    loaderOptions.flags = LoaderFlags.WithoutEditor;
  }
  const loader = createLoader(loaderOptions);
  const instance = await loader.loadAsync(data);
  return instance;
}

/**
 * Spawns a prefab actor.
 * @param prefabName - The name of the prefab
 * @param transform - The transform of the actor
 * @param prefabs - The prefabs to use
 * @param isEditor - Whether this is for the editor
 * @returns The spawned actor
 */
export async function spawnPrefabActor(
  prefabName: string,
  transform?: { position?: THREE.Vector3, rotation?: THREE.Euler, scale?: THREE.Vector3 },
  prefabs: Record<string, any> = {},
  isEditor?: boolean
): Promise<Actor | null> {
  const prefabData = prefabs[prefabName];
  if (isLegacyData(prefabData)) {
    throw new Error('Legacy data is no longer supported');
  }

  return instantiatePrefab(prefabName, prefabs, {
    position: transform?.position,
    rotation: transform?.rotation,
    scale: transform?.scale,
  });
}